import type { AssignedRiskPreviousInsuranceViewModel } from 'src/api/types/AssignedRiskPreviousInsuranceViewModel';

import { useParams } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import Skeleton from '@mui/material/Skeleton';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TableContainer from '@mui/material/TableContainer';

import { MESSAGES } from 'src/constants/messages';
import { useAssignedRiskApplicationInsuranceHistory } from 'src/api/hooks/useAssignedRiskApplicationQueries';

import { TableNoData } from 'src/components/table';

import { formatDate, formatDollarAmount } from '../../shared/utils/policyUtils';

type InsuranceHistoryColumn = {
  header: string;
  testId: string;
  getValue: (row: AssignedRiskPreviousInsuranceViewModel) => string;
};

const insuranceMessages = MESSAGES.ASSIGNED_RISK_APPLICATION.INSURANCE_HISTORY;

const TABLE_COLUMNS: InsuranceHistoryColumn[] = [
  { header: insuranceMessages.TABLE_HEADERS.CARRIER_NAME, testId: 'insurance-history-carrier', getValue: (row) => row.carrierName ?? '' },
  {
    header: insuranceMessages.TABLE_HEADERS.STATE,
    testId: 'insurance-history-state',
    getValue: (row) => row.stateAbbreviation ?? row.stateName ?? '',
  },
  { header: insuranceMessages.TABLE_HEADERS.POLICY_NUMBER, testId: 'insurance-history-policy-number', getValue: (row) => row.policyNumber ?? '' },
  {
    header: insuranceMessages.TABLE_HEADERS.FROM,
    testId: 'insurance-history-effective-date',
    getValue: (row) => formatDate(row.coverageEffectiveDate),
  },
  {
    header: insuranceMessages.TABLE_HEADERS.TO,
    testId: 'insurance-history-expiration-date',
    getValue: (row) => formatDate(row.coverageExpirationDate),
  },
  {
    header: insuranceMessages.TABLE_HEADERS.ANNUAL_PREMIUM,
    testId: 'insurance-history-premium',
    getValue: (row) => formatDollarAmount(row.premium),
  },
];

type InsuranceHistoryProps = {
  assignedRiskAppId?: number;
};

export default function InsuranceHistory({ assignedRiskAppId: assignedRiskAppIdProp }: InsuranceHistoryProps = {}) {
  const params = useParams<{
    assignedRiskAppId: string;
  }>();
  const assignedRiskAppId = assignedRiskAppIdProp ?? Number(params.assignedRiskAppId);

  const { data, isLoading, isError } = useAssignedRiskApplicationInsuranceHistory(assignedRiskAppId);
  const insuranceHistory = data?.data ?? [];

  return (
    <Card
      sx={{
        display: 'flex',
        zIndex: 'unset',
        overflow: 'unset',
        mt: 5,
        borderRadius: 2,
        boxShadow: 'var(--customShadows-card)',
      }}
      data-testid="insurance-history-card"
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary' }}>
          {insuranceMessages.TITLE}
        </Typography>

        {isLoading ? (
          <Box data-testid="insurance-history-loading">
            <Skeleton variant="rectangular" height={48} sx={{ mt: 3 }} />
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="rectangular" height={48} />
          </Box>
        ) : null}
        {isError ? <Alert severity="error" data-testid="insurance-history-error">{insuranceMessages.LOAD_ERROR}</Alert> : null}
        {!isLoading && !isError ? <TableContainer
          sx={(theme) => ({
            mt: 3,
            overflowX: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          })}
        >
          <Table data-testid="insurance-history-table" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <TableCell key={column.header} sx={{ width: `${100 / TABLE_COLUMNS.length}%`, whiteSpace: 'nowrap' }}>
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {insuranceHistory.length > 0 ? (
                insuranceHistory.map((row, index) => (
                  <TableRow key={`${row.policyNumber ?? ''}-${index}`}>
                    {TABLE_COLUMNS.map((column) => (
                      <TableCell key={column.testId} data-testid={`${column.testId}-${index}`}>
                        {column.getValue(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableNoData notFound data-testid="insurance-history-no-data" />
              )}
            </TableBody>
          </Table>
        </TableContainer> : null}
      </CardContent>
    </Card>
  );
}
